import React, { useState } from 'react';
import { FieldsCardProps } from 'types/DetailPageTypes';
import { useTranslation } from 'react-i18next';
import { useModal } from 'components/modals/ModalContext';
import { useAuthContext } from 'services/authentication/AuthenticationContext';
import { useSettings } from 'services/settings/SettingsContext';
import { fetchData } from 'services/api/fetchData';
import { saveData } from 'services/api/saveData';
import { encryptData } from 'services/authentication/encryptData';
import PhotoViewModal, { PhotoViewType } from 'components/modals/PhotoViewModal';
import IconButton from 'components/buttons/IconButton';
import TabsButton from 'components/buttons/TabsButton';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleQuestion, faList } from '@fortawesome/free-solid-svg-icons';
import { faDownload, faGrid2 } from '@fortawesome/pro-solid-svg-icons';
import '../../style/scss/card.scss';

interface PhotoType {
    id: number;
    created: Date;
    modified: Date;
    file: string;
    thumbnail: string | null;
    filename: string;
    description: string;
    created_by_user_full_name: string | null;
    custom_field_answer: number | null;
    form_sheet_photo: boolean;
    job: number | null;
    deleted: boolean;
}

const PhotosCard: React.FC<FieldsCardProps> = ({
    viewKey, itemId, isEditable, data, loadStatus, refetchData
}) => {
    const { t } = useTranslation();
    const { initializeModal } = useModal();
    const { handleLogout, userHash } = useAuthContext();
    const { userPreferences, setUserPreferences } = useSettings();
    const [downloading, setDownloading] = useState(false);

    // Get the display mode from the user preferences
    const displayMode = userPreferences.job_photos_display_mode || 'grid';

    // Function to format the date based on the local timezone and format
    const formatDate = (dateString: Date) => {
        const date = new Date(dateString);
        return date.toLocaleString([], { dateStyle: 'short', timeStyle: 'short' });
    };

    // Get the visible job photos without the form photos
    const jobPhotos = (data?.photos || []).filter(
        (photo: PhotoType) => !photo.deleted && !photo.custom_field_answer
    );

    // Count the photos which were added to the filled forms
    const formPhotosCount = (data?.photos || []).filter(
        (photo: PhotoType) => !photo.deleted && photo.form_sheet_photo
    ).length;

    // Handle viewing of a photo
    const handleViewPhoto = (event: React.MouseEvent, photo: PhotoType) => {
        // Prevent edit-view mode toggling
        event.preventDefault();
        event.stopPropagation();

        // Open the photo view modal
        initializeModal(
            <PhotoViewModal
                photo={photo as PhotoViewType}
                photos={jobPhotos as PhotoViewType[]}
                editable={isEditable?.editability !== false}
                onSaved={() => refetchData && refetchData()}
                onDeleted={() => refetchData && refetchData()}
            />, {
                modalSize: 'medium',
                modalPosition: 'center',
                modalSpacing: 'compact',
            }
        );
    }

    // Save the chosen display mode in the user preferences
    const handleDisplayModeChange = async (mode: 'grid' | 'list') => {
        // Skip saving when the mode did not change
        if (mode === displayMode) return;

        // Optimistically update the user preferences state
        const updatedPreferences = { ...userPreferences, job_photos_display_mode: mode };
        setUserPreferences(updatedPreferences);

        try {
            // Save the display mode in the user preference model
            const response: any = await saveData({
                apiUrl: `patch_user_preference/${userHash}`,
                method: 'patch',
                data: { job_photos_display_mode: mode },
            });

            // Store the updated preferences encrypted in the local storage
            if (response?.status === 200) {
                const preferencesString = JSON.stringify(response.data);
                localStorage.setItem('user_preferences', encryptData(preferencesString));
            }
        } catch (error) {
            console.error(error);
        }
    };

    // Download the job photos as separate files in a zip
    const handleDownloadPhotos = async (event: React.MouseEvent) => {
        // Prevent edit-view mode toggling
        event.preventDefault();
        event.stopPropagation();

        // Skip when a download is already running
        if (downloading) return;

        try {
            setDownloading(true);

            // Request the zip file from the backend
            const blob = await fetchData({
                apiUrl: `get_job_photos_download/${itemId}`,
                responseAsBlob: true,
                handleLogout,
            });

            // Create a temporary link to trigger the download
            const url = window.URL.createObjectURL(new Blob([blob], { type: 'application/zip' }));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `${data?.number || itemId}_photos.zip`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error(error);
        } finally {
            setDownloading(false);
        }
    };

    // Render a single photo in the grid view
    const renderGridPhoto = (photo: PhotoType) => (
        <div key={photo.id}
            onClick={e => handleViewPhoto(e, photo)}
            className='photo-box tooltip-icon'>
            <img src={photo.thumbnail || photo.file}
                 alt={photo.filename}
                 loading='lazy'
                 decoding='async'
                 className='photo' />
            <span className="tooltip tooltip-bottom">
                {photo.description ? (
                    <div>{photo.description}</div>
                ) : (
                    <div>{photo.filename}</div>
                )}
                <div>{t('job.photos.photo_added_label')} {formatDate(photo.created)}</div>
            </span>
        </div>
    );

    // Render a single photo in the list view
    const renderListPhoto = (photo: PhotoType) => (
        <div key={photo.id}
            onClick={e => handleViewPhoto(e, photo)}
            className='photo-list-row'>
            <img src={photo.thumbnail || photo.file}
                 alt={photo.filename}
                 loading='lazy'
                 decoding='async'
                 className='photo-list-thumbnail' />
            <div className='photo-list-details'>
                {photo.description && (
                    <div className='photo-list-description'>
                        {photo.description}
                    </div>
                )}
                <div className='photo-list-meta'>
                    {photo.created_by_user_full_name && (
                        <span>{photo.created_by_user_full_name}{' - '}</span>
                    )}
                    <span>{formatDate(photo.created)}</span>
                </div>
            </div>
        </div>
    );

    return (
        (jobPhotos.length > 0 || formPhotosCount > 0) ? (
            <div className='card photoscard'>
                <div className='photos-header'>
                    <h6>{t('job.photos.photos_label')}</h6>
                    <span className="tooltip-icon">
                        <FontAwesomeIcon icon={faCircleQuestion} />
                        <span className="tooltip tooltip-right">{t('job.photos.photos_explanation_tooltip')}</span>
                    </span>

                    {jobPhotos.length > 0 && (
                        <div className='photos-header-actions'>
                            <IconButton
                                type='button'
                                onClick={handleDownloadPhotos}
                                icon={<FontAwesomeIcon icon={faDownload} />}
                                label='job.photos.download_photos_label'
                                size='extra-small'
                                loading={downloading}
                            />
                            <TabsButton
                                buttons={[
                                    {
                                        icon: <FontAwesomeIcon icon={faGrid2} />,
                                        tabValue: 'grid',
                                        tooltipText: 'job.photos.view_grid_tooltip',
                                    },
                                    {
                                        icon: <FontAwesomeIcon icon={faList} />,
                                        tabValue: 'list',
                                        tooltipText: 'job.photos.view_list_tooltip',
                                    },
                                ]}
                                currentTabValue={displayMode}
                                onTabsClick={tabValue => handleDisplayModeChange(tabValue as 'grid' | 'list')}
                                size='extra-small'
                            />
                        </div>
                    )}
                </div>
                {loadStatus === "loading" ? (
                    <div className="loader"></div>
                ) : loadStatus === "loaded" && data && (
                    <>
                        {jobPhotos.length > 0 && (
                            displayMode === 'list' ? (
                                <div className='photos-rows'>
                                    {jobPhotos.map((photo: PhotoType) => renderListPhoto(photo))}
                                </div>
                            ) : (
                                <div className='photos-list'>
                                    {jobPhotos.map((photo: PhotoType) => renderGridPhoto(photo))}
                                </div>
                            )
                        )}

                        {formPhotosCount > 0 && (
                            <div className='photos-form-subtitle'>
                                {t('job.photos.form_photos_subtitle', { count: formPhotosCount })}
                            </div>
                        )}
                    </>
                )}
            </div>
        ) : (null)
    );
}

export default PhotosCard;
